import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiUsers, FiSearch, FiRefreshCw, FiEdit, FiX } from 'react-icons/fi';
import { supabase } from '../../config/supabase';

const Container = styled.div`
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  gap: 8px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  width: 280px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const TableContainer = styled.div`
  ${scrollableTableWrapStyles}
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
`;

const Row = styled.tr`
  transition: background 0.2s;
  &:hover { background: #f9fafb; }
  
  &:last-child td {
    border-bottom: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const AvatarImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e5e7eb;
`;

const AvatarPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  border: 2px solid #e5e7eb;
`;

const ProfileId = styled.code`
  font-size: 11px;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
  color: #6b7280;
  font-family: 'Courier New', monospace;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #5efba9;
  color: #1f2937;
  font-weight: 500;

  &:hover {
    background: #4de8a8;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  padding: 32px;
  position: relative;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
    cursor: not-allowed;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #5efba9;
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #5efba9;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 24px auto;
  padding: 12px 32px;
  background: #5efba9;
  border: 1px solid #5efba9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4dd699;
    border-color: #4dd699;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &.primary {
    background: #5efba9;
    color: #1f2937;
    border: none;

    &:hover {
      background: #4de8a8;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border: 1px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
    }
  }
`;

export default function SuperAdminUsers() {
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [search, setSearch] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [displayedCount, setDisplayedCount] = useState(20);
  const [phoneFieldKey, setPhoneFieldKey] = useState('phone_number');
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    is_admin: false,
    is_superadmin: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setProfiles(data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      alert('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredProfiles = profiles.filter(profile => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    // Search across all string fields
    return Object.values(profile || {}).some(value => 
      value && String(value).toLowerCase().includes(s)
    );
  });

  // Reset displayed count when search changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [search]);

  // Slice to show only displayedCount items
  const displayedProfiles = filteredProfiles.slice(0, displayedCount);
  const hasMoreProfiles = filteredProfiles.length > displayedCount;

  // Get all column names from the first profile (or empty array if no profiles)
  // Exclude avatar_url since we have a dedicated Avatar column
  // Also exclude username and updated_at as requested
  const allColumns = profiles.length > 0 
    ? Object.keys(profiles[0]).filter(col => 
        col !== 'avatar_url' && 
        col !== 'username' && 
        col !== 'website' &&
        col !== 'id' &&
        col !== 'updated_at'
      )
    : [];

  // Define preferred column order (most important first)
  const preferredOrder = ['full_name', 'email', 'is_admin', 'is_superadmin', 'created_at'];
  const orderedColumns = allColumns.length > 0 
    ? [
        ...preferredOrder.filter(col => allColumns.includes(col)),
        ...allColumns.filter(col => !preferredOrder.includes(col))
      ]
    : [];

  // Format column name for display
  const formatColumnName = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format cell value for display
  const formatCellValue = (key, value) => {
    // Handle IDs (make them compact)
    if (key === 'id') {
      if (!value) return '-';
      return <ProfileId>{value.substring(0, 8)}...</ProfileId>;
    }
    
    // Handle booleans first (including null as false)
    if (key === 'is_admin' || key === 'is_superadmin' || (typeof key === 'string' && key.startsWith('is_'))) {
      // Explicitly check for true, everything else (false, null, undefined) is "No"
      return value === true ? 'Yes' : 'No';
    }
    
    if (value === null || value === undefined || value === '') return '-';
    
    // Handle dates
    if (key.includes('_at') || key.includes('date')) {
      return formatDate(value);
    }
    
    // Handle URLs (but not avatar_url since we have Avatar column)
    if ((key.includes('url') || key.includes('website')) && key !== 'avatar_url') {
      if (!value) return '-';
      return (
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#5efba9', textDecoration: 'none', fontSize: '12px' }}
        >
          Link
        </a>
      );
    }
    
    // Handle booleans (non-null values)
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // Handle objects/arrays
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      return <span style={{ fontSize: '11px', color: '#6b7280' }}>{JSON.stringify(value)}</span>;
    }
    
    // Default: return as string
    return String(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleEdit = (profile) => {
    console.log('Editing profile:', profile);
    console.log('is_admin value:', profile.is_admin, 'type:', typeof profile.is_admin);
    console.log('is_superadmin value:', profile.is_superadmin, 'type:', typeof profile.is_superadmin);
    
    setEditingProfile(profile);

    const detectedPhoneKey =
      profile && typeof profile === 'object'
        ? (Object.prototype.hasOwnProperty.call(profile, 'phone_number')
            ? 'phone_number'
            : Object.prototype.hasOwnProperty.call(profile, 'phone')
              ? 'phone'
              : Object.prototype.hasOwnProperty.call(profile, 'mobile')
                ? 'mobile'
                : 'phone_number')
        : 'phone_number';
    setPhoneFieldKey(detectedPhoneKey);

    // Populate all editable fields
    setFormData({
      full_name: profile.full_name || '',
      phone: (profile?.[detectedPhoneKey] || '').toString(),
      is_admin: profile.is_admin === true,
      is_superadmin: profile.is_superadmin === true
    });
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingProfile(null);
    setPhoneFieldKey('phone_number');
    setFormData({
      full_name: '',
      phone: '',
      is_admin: false,
      is_superadmin: false
    });
  };

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    console.log('Checkbox changed:', { name, type, checked, value });
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      console.log('Updated formData:', newData);
      return newData;
    });
  };
  
  const handleTextInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingProfile) return;

    try {
      setSubmitting(true);
      
      console.log('Updating profile with data:', {
        id: editingProfile.id,
        is_admin: formData.is_admin,
        is_superadmin: formData.is_superadmin
      });
      
      // Ensure boolean values are explicitly true or false
      const updateData = {
        full_name: formData.full_name || null,
        [phoneFieldKey]: formData.phone || null,
        is_admin: formData.is_admin === true ? true : false,
        is_superadmin: formData.is_superadmin === true ? true : false
      };
      
      console.log('Updating profile:', {
        id: editingProfile.id,
        updateData,
        formData
      });
      
      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', editingProfile.id)
        .select();

      if (error) {
        console.error('❌ Supabase update error:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        
        // Check for RLS policy errors
        if (error.code === 'PGRST116' || error.message?.includes('row-level security') || error.message?.includes('permission denied')) {
          alert('Permission denied. Please check your database RLS policies. Only superadmins can update user roles.');
          throw new Error('RLS Policy Error: Update was blocked by row-level security policy.');
        }
        
        throw error;
      }

      // Verify the update actually worked
      if (!data || data.length === 0) {
        console.warn('⚠️ Update returned no data - this might indicate an RLS policy issue');
        alert('Update may have failed. Please check the database or refresh the page.');
        await fetchProfiles();
        handleCloseModal();
        return;
      }

      const updatedProfile = data[0];
      console.log('✅ Update successful!');
      console.log('Updated profile:', updatedProfile);
      console.log('is_admin in DB:', updatedProfile.is_admin, 'type:', typeof updatedProfile.is_admin);
      console.log('is_superadmin in DB:', updatedProfile.is_superadmin, 'type:', typeof updatedProfile.is_superadmin);
      
      // Verify the values were actually updated
      if (updatedProfile.is_admin !== updateData.is_admin || updatedProfile.is_superadmin !== updateData.is_superadmin) {
        console.warn('⚠️ Values in database do not match what was sent!');
        console.warn('Expected is_admin:', updateData.is_admin, 'Got:', updatedProfile.is_admin);
        console.warn('Expected is_superadmin:', updateData.is_superadmin, 'Got:', updatedProfile.is_superadmin);
        alert('Warning: The update may not have been applied correctly. Please refresh and check the database.');
      }

      // Always refetch to ensure we have the latest data from database
      await fetchProfiles();

      alert('Profile updated successfully!');
      handleCloseModal();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>
          <FiUsers /> Users Management
        </Title>
        <HeaderActions>
          <SearchBar>
            <FiSearch />
            <SearchInput
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBar>
          <IconButton onClick={fetchProfiles}>
            <FiRefreshCw /> Refresh
          </IconButton>
        </HeaderActions>
      </Header>

      <Card>
        <TableContainer>
          <Table>
            <thead>
              <Row>
                {orderedColumns.length > 0 ? (
                  <>
                    <Th style={{ width: '60px' }}>Avatar</Th>
                    {orderedColumns.map(column => (
                      <Th key={column}>{formatColumnName(column)}</Th>
                    ))}
                    <Th style={{ width: '100px' }}>Actions</Th>
                  </>
                ) : (
                  <Th>Loading...</Th>
                )}
              </Row>
            </thead>
            <tbody>
              {loading ? (
                <Row>
                  <Td colSpan={orderedColumns.length + 2 || 1}>
                    <EmptyState>Loading users...</EmptyState>
                  </Td>
                </Row>
              ) : displayedProfiles.length === 0 ? (
                <Row>
                  <Td colSpan={orderedColumns.length + 2 || 1}>
                    <EmptyState>
                      <EmptyIcon>👥</EmptyIcon>
                      <EmptyTitle>No Users Found</EmptyTitle>
                      <EmptyText>
                        {search ? 'No users match your search criteria' : 'No users registered yet'}
                      </EmptyText>
                    </EmptyState>
                  </Td>
                </Row>
              ) : (
                displayedProfiles.map(profile => (
                  <Row key={profile.id}>
                    <Td>
                      {profile.avatar_url ? (
                        <AvatarImg src={profile.avatar_url} alt={profile.full_name || 'User'} />
                      ) : (
                        <AvatarPlaceholder>
                          {(profile.full_name || profile.email || 'U').toString().trim().charAt(0).toUpperCase()}
                        </AvatarPlaceholder>
                      )}
                    </Td>
                    {orderedColumns.map(column => (
                      <Td key={column}>
                        {formatCellValue(column, profile[column])}
                      </Td>
                    ))}
                    <Td>
                      <ActionButton onClick={() => handleEdit(profile)}>
                        <FiEdit /> Edit
                      </ActionButton>
                    </Td>
                  </Row>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Card>

      {!loading && hasMoreProfiles && (
        <LoadMoreButton onClick={() => setDisplayedCount(prev => prev + 20)}>
          Load More ({filteredProfiles.length - displayedCount} remaining)
        </LoadMoreButton>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && editingProfile && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit User Profile</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleTextInputChange}
                  placeholder="Enter full name"
                />
              </FormGroup>

              <FormGroup>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleTextInputChange}
                  placeholder="Enter phone number"
                />
              </FormGroup>

              <FormGroup>
                <Label>Permissions</Label>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    name="is_admin"
                    checked={formData.is_admin === true}
                    onChange={handleInputChange}
                  />
                  <span>Is Admin</span>
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    name="is_superadmin"
                    checked={formData.is_superadmin === true}
                    onChange={handleInputChange}
                  />
                  <span>Is Super Admin</span>
                </CheckboxLabel>
                <div style={{ 
                  marginTop: '8px',
                  padding: '12px',
                  background: '#fef3c7',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#92400e'
                }}>
                  <strong>Note:</strong> Super Admin privileges override Admin privileges. Users with Super Admin access have full system control.
                </div>
              </FormGroup>

              <ButtonGroup>
                <Button type="button" className="secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" className="primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
}
